import { createClient } from '@supabase/supabase-js'

// Supabase URL và API key từ biến môi trường
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://aggkdikmvlbtaumbbszf.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFnZ2tkaWttdmxidGF1bWJic3pmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MTc5MDU3MzQsImV4cCI6MjAzMzQ4MTczNH0.GrDrkPMoTxZ_LCq9-TZDkzx2TnhA6g0DdP7f0YQSoFc'

// Tạo client Supabase
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
  },
  global: {
    headers: {
      'Content-Type': 'application/json',
    },
  },
  // Cấu hình CORS cho Edge Functions
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
})

// Cấu hình cache
const CACHE_CONFIG = {
  enabled: true,
  ttl: 5 * 60 * 1000, // 5 phút
  enableLogs: false
}

// Cache trong memory
const memoryCache = new Map<string, {
  data: any,
  timestamp: number,
  expiresAt: number
}>();

// Hàm tạo khóa cache
function createCacheKey(tableName: string, columns: string, options?: any): string {
  const key = `${tableName}_${columns}_${JSON.stringify(options || {})}`;
  return key;
}

// Hàm lấy dữ liệu từ cache
function getFromCache<T>(key: string): T | null {
  if (!CACHE_CONFIG.enabled) return null;
  
  const cachedItem = memoryCache.get(key);
  if (!cachedItem) return null;
  
  // Kiểm tra hết hạn
  if (Date.now() > cachedItem.expiresAt) {
    memoryCache.delete(key);
    return null;
  }
  
  return cachedItem.data as T;
}

// Hàm lưu dữ liệu vào cache
function setToCache<T>(key: string, data: T): void {
  if (!CACHE_CONFIG.enabled) return;
  
  memoryCache.set(key, {
    data,
    timestamp: Date.now(),
    expiresAt: Date.now() + CACHE_CONFIG.ttl
  });
}

// Hàm tiện ích để kiểm tra kết nối
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.from('categories').select('count', { count: 'exact', head: true })
    return !error
  } catch (error) {
    console.error('Không thể kết nối tới Supabase:', error)
    return false
  }
}

// Hàm lấy dữ liệu từ bảng với cache
export async function fetchFromTable(tableName: string, columns: string = '*', options?: any) {
  try {
    // Tạo khóa cache
    const cacheKey = createCacheKey(tableName, columns, options);
    
    // Kiểm tra cache
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // Không có trong cache, query từ Supabase
    let query = supabase.from(tableName).select(columns)
    
    if (options) {
      if (options.where) {
        for (const [key, value] of Object.entries(options.where)) {
          query = query.eq(key, value)
        }
      }
      
      if (options.limit) {
        query = query.limit(options.limit)
      }
      
      if (options.order) {
        const { column, ascending = true } = options.order
        query = query.order(column, { ascending })
      }
    }
    
    const { data, error } = await query
    
    if (error) {
      throw error
    }
    
    // Lưu vào cache
    setToCache(cacheKey, data);
    
    return data
  } catch (error) {
    console.error(`Lỗi khi lấy dữ liệu từ bảng ${tableName}:`, error)
    return null
  }
} 